import React from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import { Button, RefreshIcon, Skeleton, Tooltip } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'
import { useTranslation } from '@root/shared/localization'

import type { Error } from '@root/shared'

type Props = {
  title: string
  tooltip?: string
  value: AsyncStateN.AsyncStateN<
    Error.UnknownError,
    { el: React.ReactNode; trend: Trend.Trend | undefined }
  >
  valueSecondary: AsyncStateN.AsyncStateN<
    Error.UnknownError,
    { el: React.ReactNode; trend: Trend.Trend | undefined }
  >
  onRetry: () => void
}

export const ActivePositionDetail: React.FC<Props> = ({
  title,
  value,
  valueSecondary,
  onRetry,
  tooltip,
}) => {
  const { t } = useTranslation()
  const renderValue = () => {
    if (AsyncStateN.isSuccess(value) && AsyncStateN.isSuccess(valueSecondary)) {
      return (
        <>
          <Value $trend={value.value.trend}>{value.value.el}</Value>
          <ValueSecondary $trend={valueSecondary.value.trend}>
            {valueSecondary.value.el}
          </ValueSecondary>
        </>
      )
    }
    if (AsyncStateN.isError(value)) {
      return (
        <Value $trend={Trend.Down}>
          <span>{t('common.error')}</span>
          <Button
            variant="ghost"
            size="xxs"
            onClick={onRetry}
            iconLeft={<RefreshIcon />}
          />
        </Value>
      )
    }

    return <Skeleton width={40} height={12} paddingBlock={1} />
  }
  return (
    <Root>
      <Tooltip content={tooltip}>
        <Label>{title}</Label>
      </Tooltip>
      {renderValue()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  align-content: center;
  grid-gap: 2px;
  border-radius: 10px;
  background: var(--background-1);
  height: 60px;
  justify-content: center;
  justify-items: center;
`

const Label = styled.span`
  color: var(--text-11);
  text-align: center;

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const Value = styled.span<{ $trend: Trend.Trend | undefined }>`
  color: ${({ $trend }) =>
    match($trend)
      .with(Trend.Up, () => 'var(--up-9)')
      .with(Trend.Down, () => 'var(--down-9)')
      .with(Trend.Neutral, () => 'var(--neutral-11)')
      .otherwise(() => 'var(--text-11)')};
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  text-align: center;

  display: grid;
  grid-auto-flow: column;
  gap: 2px;
  align-items: center;
`
const ValueSecondary = styled(Value)`
  font-size: 9px;
`
